import { useProficiencyAssessment } from '@/features/nectar/hooks/useProficiencyAssessment'
import { Button, Spinner, Text } from '@/ui-kit'
import type { ProjectFlowProps } from '../project-flows'
import { PROJECT_FLOW_COMPONENTS } from '../project-flows'
import { AssessmentCountdown } from './AssessmentCountdown'
import { PhaseClaimReward } from './PhaseClaimReward'
import { PhaseResults } from './PhaseResults'
import { PhaseSetup } from './PhaseSetup'
import { PhaseSpeechRecording } from './PhaseSpeechRecording'
import { PhaseTextQuestion } from './PhaseTextQuestion'

const ProficiencyAssessmentFlow = ({ projectId, language, onBack, onCompleted }: ProjectFlowProps) => {
  const {
    phase,
    assessmentId,
    expiresAt,
    finalResult,
    starting,
    claiming,
    startAssessment,
    claimReward,
    handleTextCompleted,
    handleAllRecorded
  } = useProficiencyAssessment({ projectId, language, onBack, onCompleted })

  const showTimer =
    expiresAt && (phase === 'text-phase' || phase === 'speech-phase' || phase === 'completing')

  return (
    <div className="flex flex-col" style={{ animation: 'nectar-slide-up 0.4s ease-out' }}>
      {showTimer && (
        <div className="flex justify-end py-2">
          <AssessmentCountdown expiresAt={expiresAt} />
        </div>
      )}

      {phase === 'loading' && (
        <div className="flex w-full justify-center py-12">
          <Spinner />
        </div>
      )}

      {phase === 'setup' && <PhaseSetup onStart={startAssessment} starting={starting} />}

      {phase === 'claim-reward' && finalResult && (
        <PhaseClaimReward finalResult={finalResult} claiming={claiming} onClaim={claimReward} />
      )}

      {phase === 'text-phase' && assessmentId && (
        <PhaseTextQuestion key={assessmentId} assessmentId={assessmentId} onTextCompleted={handleTextCompleted} />
      )}

      {phase === 'speech-phase' && assessmentId && (
        <PhaseSpeechRecording
          key={`speech-${assessmentId}`}
          assessmentId={assessmentId}
          onAllRecorded={handleAllRecorded}
        />
      )}

      {phase === 'completing' && (
        <div className="flex w-full justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <Spinner />
            <Text type="body2-regular" className="text-gray-80">
              Evaluating your responses...
            </Text>
          </div>
        </div>
      )}

      {phase === 'results' && finalResult && <PhaseResults finalResult={finalResult} onBack={onBack} />}

      {phase === 'results' && !finalResult && (
        <div
          className="mx-auto flex w-full max-w-[600px] flex-col items-center gap-4 rounded-2xl border border-solid border-gray-100 px-10 py-12 text-center"
          style={{ animation: 'nectar-slide-up 0.4s ease-out' }}
        >
          <Text type="h5" className="text-gray-70">
            Assessment Completed
          </Text>
          <Text type="body2-regular" className="text-gray-80">
            You have already completed this assessment.
          </Text>
          <Button size="small" onClick={onBack} className="mt-2">
            Back to Missions
          </Button>
        </div>
      )}
    </div>
  )
}

PROJECT_FLOW_COMPONENTS.set('PROFICIENCY_ASSESSMENT', ProficiencyAssessmentFlow)

export { ProficiencyAssessmentFlow }
